import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { Close as CloseIcon, Download as DownloadIcon } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { PDFDownloadLink, pdf, PDFViewer } from '@react-pdf/renderer';
import HallTicketPdf from './HallTicketPdf';

// Student type matching API response
interface Student {
  id: string;
  fullName: string;
  name?: string; // Legacy field
  grade?: string;
  class?: string;
  section?: string;
  phoneNumber?: string;
  securityPin?: string;
  subjects?: string[];
}

const GRADES = [
  'Class-01', 'Class-02', 'Class-03', 'Class-04', 'Class-05', 'Class-06',
  'Class-07', 'Class-08', 'Class-09', 'Class-10', 'Class-11', 'Class-12',
];

// PDF Preview Modal Component
const PDFPreviewModal: React.FC<{
  students: Student[];
  schoolName: string;
  year: string;
  grade: string;
  pdfCache: Record<string, string>;
  setPdfCache: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}> = ({ students, schoolName, year, grade, pdfCache, setPdfCache }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);

  const handlePreview = async () => {
    setIsOpen(true);

    // Check if PDF is already cached
    if (pdfCache[grade]) {
      console.log(`Using cached PDF for ${grade}`);
      return; // PDF already exists, just show it
    }

    // For very large PDFs, show a warning
    if (students.length > 300 && grade === 'ALL_SCHOOL') {
      const confirmed = window.confirm(
        `This will generate a very large PDF with ${students.length} students (${Math.ceil(students.length / 9)} pages).\n\n` +
        `This may take several minutes and could make your browser temporarily unresponsive.\n\n` +
        `Do you want to continue?`
      );
      if (!confirmed) {
        setIsOpen(false);
        return;
      }
    }

    // Generate new PDF
    console.log(`Generating new PDF for ${grade}`);
    setIsGenerating(true);
    setProgress(0);

    try {
      // Students are already sorted from backend, use them directly for better performance
      const sortedStudents = students;
      const totalStudents = sortedStudents.length;

      // For large student lists, use chunked processing to prevent browser freeze
      if (totalStudents > 100) {
        await generatePDFInChunks(sortedStudents, totalStudents);
      } else {
        await generatePDFDirectly(sortedStudents);
      }

    } catch (error) {
      console.error('PDF generation failed:', error);
      setIsGenerating(false);
      setProgress(0);
    }
  };

  // Direct PDF generation for smaller lists
  const generatePDFDirectly = async (sortedStudents: Student[]) => {
    // Simple progress animation
    const progressInterval = setInterval(() => {
      setProgress(prev => Math.min(prev + 10, 90));
    }, 200);

    // Generate PDF blob and cache it
    const pdfDoc = <HallTicketPdf students={sortedStudents} schoolName={schoolName} year={year} />;
    const blob = await pdf(pdfDoc).toBlob();
    const pdfUrl = URL.createObjectURL(blob);

    // Cache the PDF URL
    setPdfCache(prev => ({
      ...prev,
      [grade]: pdfUrl
    }));

    clearInterval(progressInterval);
    setProgress(100);
    setTimeout(() => {
      setIsGenerating(false);
    }, 500);
  };

  // Chunked PDF generation for large lists to prevent browser freeze
  const generatePDFInChunks = async (sortedStudents: Student[], totalStudents: number) => {
    return new Promise<void>((resolve, reject) => {
      let currentProgress = 0;

      // Update progress more frequently for better UX
      const updateProgress = (percentage: number) => {
        currentProgress = Math.min(percentage, 90);
        setProgress(currentProgress);
      };

      // Use multiple setTimeout calls to yield control back to browser
      const processWithYielding = async () => {
        try {
          // Step 1: Preparation
          updateProgress(10);
          await new Promise(resolve => setTimeout(resolve, 100));

          // Step 2: Pre-processing
          updateProgress(20);
          await new Promise(resolve => setTimeout(resolve, 100));

          // Step 3: Start PDF generation
          updateProgress(30);
          await new Promise(resolve => setTimeout(resolve, 100));

          // Step 4: Generate PDF document (this is the heavy part)
          const pdfDoc = <HallTicketPdf students={sortedStudents} schoolName={schoolName} year={year} />;

          updateProgress(50);
          await new Promise(resolve => setTimeout(resolve, 200)); // Longer pause before heavy operation

          // Step 5: Convert to blob (heavy operation)
          updateProgress(70);
          const blob = await pdf(pdfDoc).toBlob();

          updateProgress(85);
          await new Promise(resolve => setTimeout(resolve, 100));

          // Step 6: Create URL and cache
          const pdfUrl = URL.createObjectURL(blob);

          // Cache the PDF URL
          setPdfCache(prev => ({
            ...prev,
            [grade]: pdfUrl
          }));

          updateProgress(100);
          setTimeout(() => {
            setIsGenerating(false);
            resolve();
          }, 500);

        } catch (error) {
          reject(error);
        }
      };

      // Start processing with a small delay to show initial progress
      setTimeout(processWithYielding, 100);
    });
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsGenerating(false);
    setProgress(0);
  };

  return (
    <Box>
      <Button
        variant="contained"
        color="primary"
        sx={{ mt: 2 }}
        fullWidth
        onClick={handlePreview}
      >
        Preview Hall Tickets
      </Button>

      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Hall Tickets Preview - {grade === 'ALL_SCHOOL' ? 'Complete School' : grade} ({students.length} students)
          </Typography>
          <Box>
            {pdfCache[grade] ? (
              // Use cached PDF for download
              <IconButton
                color="primary"
                disabled={isGenerating}
                sx={{ mr: 1 }}
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = pdfCache[grade];
                  const filename = grade === 'ALL_SCHOOL'
                    ? `Hall Tickets - ${schoolName}.pdf`
                    : `Hall Tickets - ${schoolName} - ${grade}.pdf`;
                  link.download = filename;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                <DownloadIcon />
              </IconButton>
            ) : (
              // Fallback to PDFDownloadLink
              <PDFDownloadLink
                document={<HallTicketPdf students={students} schoolName={schoolName} year={year} />}
                fileName={grade === 'ALL_SCHOOL'
                  ? `Hall Tickets - ${schoolName}.pdf`
                  : `Hall Tickets - ${schoolName} - ${grade}.pdf`}
              >
                {({ loading }) => (
                  <IconButton
                    color="primary"
                    disabled={loading || isGenerating}
                    sx={{ mr: 1 }}
                  >
                    <DownloadIcon />
                  </IconButton>
                )}
              </PDFDownloadLink>
            )}
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, height: '100%', position: 'relative' }}>
          {isGenerating ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                backgroundColor: '#f5f5f5'
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                Generating PDF... {Math.round(progress)}%
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Processing {students.length} student hall tickets
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ width: '60%', height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                {students.length > 100
                  ? 'Large PDF generation in progress - please wait...'
                  : 'This may take a moment for large classes...'
                }
              </Typography>
              {students.length > 200 && (
                <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                  ⚠️ Very large PDF ({Math.ceil(students.length / 9)} pages) - this may take several minutes
                </Typography>
              )}
            </Box>
          ) : pdfCache[grade] ? (
            // Show cached PDF using iframe
            <iframe
              src={pdfCache[grade]}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: '#f5f5f5'
              }}
              title={`Hall Tickets - ${grade}`}
            />
          ) : (
            // Fallback to PDFViewer for real-time generation
            <PDFViewer
              style={{ width: '100%', height: '100%', border: 'none' }}
              showToolbar={true}
            >
              <HallTicketPdf students={students} schoolName={schoolName} year={year} />
            </PDFViewer>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const ClassesPage: React.FC = () => {
  // Students are passed via navigation state from SchoolStudentsPage
  // They are already sorted by class (grade) → section → name from the backend
  const location = useLocation();
  const students: Student[] = location.state?.students || [];
  const schoolName: string = location.state?.schoolName || 'School';
  const year = '2025-26';

  // PDF Cache - stores generated PDFs by grade
  const [pdfCache, setPdfCache] = useState<Record<string, string>>({});
  const [exportingGrade, setExportingGrade] = React.useState<string | null>(null);

  // Aggregate students by grade (ignore section) and map data properly
  const studentsByGrade = React.useMemo(() => {
    const map: Record<string, Student[]> = {};
    GRADES.forEach((grade) => { map[grade] = []; });
    students.forEach((student) => {
      const grade = student.grade || student.class || 'Unknown';
      if (map[grade]) {
        // Map fullName to name for PDF compatibility, ensure name is always a string
        const mappedStudent: Student = {
          ...student,
          name: student.fullName || student.name || 'Unknown Student',
        };
        map[grade].push(mappedStudent);
      }
    });

    // Students are already sorted by class → section → name from backend
    // No additional sorting needed for better performance

    return map;
  }, [students]);

  const handleExportHallTickets = (studentsInGrade: Student[], grade: string) => {
    // TODO: Implement export/print logic for this grade
    alert(`Exporting hall tickets for ${studentsInGrade.length} students in ${grade}`);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
        Classes in School
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
        {GRADES.map((grade) => (
          <Card key={grade} sx={{ boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {grade}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Students: {studentsByGrade[grade]?.length || 0}
                  {pdfCache[grade] && (
                    <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1 }}>
                      • PDF Ready
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Subscriptions: {
                    studentsByGrade[grade]?.reduce((total, student) =>
                      total + (student.subjects?.length || 0), 0
                    ) || 0
                  }
                </Typography>
              </Box>
              {studentsByGrade[grade]?.length > 0 ? (
                <PDFPreviewModal
                  students={studentsByGrade[grade]}
                  schoolName={schoolName}
                  year={year}
                  grade={grade}
                  pdfCache={pdfCache}
                  setPdfCache={setPdfCache}
                />
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 2 }}
                  fullWidth
                  disabled
                >
                  Export Hall Tickets
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Full Width School Card */}
      <Card sx={{ mt: 4, boxShadow: 4, border: '2px solid', borderColor: 'primary.main' }}>
        <CardContent sx={{ py: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
            {schoolName} - Complete School
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body1" color="text.secondary">
              Students: {students.length}
              {pdfCache['ALL_SCHOOL'] && (
                <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1 }}>
                  • PDF Ready
                </Typography>
              )}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Subscriptions: {
                students.reduce((total, student) =>
                  total + (student.subjects?.length || 0), 0
                )
              }
            </Typography>
          </Box>
          {students.length > 0 ? (
            <PDFPreviewModal
              students={students}
              schoolName={schoolName}
              year={year}
              grade="ALL_SCHOOL"
              pdfCache={pdfCache}
              setPdfCache={setPdfCache}
            />
          ) : (
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              fullWidth
              disabled
            >
              Preview Hall Tickets
            </Button>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClassesPage; 